import React, { useState, useEffect, useContext } from "react";
import { CreatomatePropertyValue, Subtitle } from "chq";
import { useProfile } from "app/context";
import { languages as allLanguages } from "app/utils/aws-languages.json";
import general from "constants/general";
import LineHeight from "components/stories/video-studio/common/properties/text/LineHeight";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { BuiltInFontDefinition } from "components/stories/video-studio/fonts/BuiltInFonts";
import { getOrganizationSelectOptionsFonts } from "components/stories/video-studio/utils";
import { ElementState, Preview } from "@creatomate/preview";
import { classnames } from "@culturehq/components";
import LanguageSelectField from "./LanguageSelectField";
import SubtitlesFontValues from "./SubtitlesFontValues";
import { isSubtitleText, modificationsForAllSubtitles } from "../utils";
import FontEffects from "../../text/FontEffects";
import TextAligment from "../../text/TextAligment";

interface SubtitleStylesProps {
  activeLanguage?: Subtitle;
  onLanguageChange: (subtitle: Subtitle) => void;
  preview: Preview;
  subtitleElements: ElementState[];
}

const SubtitleStyles: React.FC<SubtitleStylesProps> = ({
  activeLanguage, onLanguageChange, preview, subtitleElements
}) => {
  const { data: { burnSubtitles, subtitles }, getActiveElement } = useContext(CreatomateContext);
  const activeElement = getActiveElement(preview);


  const { profile: { organization } } = useProfile();
  const [selectedEffect, setSelectedEffect] = useState(general.DEFAULT_EFFECT);
  const [parsedFonts, setParsedFonts] = useState<BuiltInFontDefinition[]>([]);
  const [selectedFont, setSelectedFont] = useState<BuiltInFontDefinition>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => loadFonts(), [organization]);

  useEffect(
    () => {
      const getFontEffect = () => {
        const { source } = activeElement as ElementState;
        if (source.background_color && !source.shadow_color && !source.stroke_color) {
          if (source.background_border_radius === "25%") {
            if (source.background_align_threshold === "0%") {
              return "Rounded background";
            }
            return "Block background";
          }
          return "Straight background";
        }
        if (!source.background_color && source.shadow_color && !source.stroke_color) {
          return "Shadow";
        }
        if (!source.background_color && !source.shadow_color && source.stroke_color) {
          if (source.stroke_width === "0.5 vmin") {
            return "Outline & Filled";
          }
          return "Outline";
        }
        return general.DEFAULT_EFFECT;
      };

      if (activeElement?.source?.type === "text"
        && isSubtitleText(activeElement?.source?.name)
        && activeElement?.source?.font_family) {
        setSelectedEffect(getFontEffect());
      }
    }, [activeElement]
  );

  useEffect(
    () => {
      if (!activeElement) {
        const activeSubtitle = subtitles.find(subtitle => subtitle.active);
        if (!activeSubtitle
          || !activeSubtitle.sections
          || activeSubtitle.sections.length === 0) return;

        const firstSection = activeSubtitle.sections[0];
        if (!firstSection.creatomateId) return;

        preview.setActiveElements([firstSection.creatomateId]);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeElement]
  );

  const handleSubtitleEffectChange = async (properties: { property: string; value: string }[]) => {
    let modifications = {};
    properties.forEach(property => {
      modifications = {
        ...modifications,
        ...modificationsForAllSubtitles(subtitleElements, property.property, property.value)
      };
    });
    await preview.applyModifications(modifications);
  };

  const handleSelectEffect = (value: string) => {
    setSelectedEffect(value);
  };

  const handleSubtitlePropChange = async (propertyName: string, value: CreatomatePropertyValue) => {
    await preview.applyModifications(
      modificationsForAllSubtitles(subtitleElements, propertyName, value)
    );
  };

  const loadFonts = (selectedValue = "") => {
    const partialFonts = getOrganizationSelectOptionsFonts(organization.name || "");

    const currentLanguage = selectedValue === "zh"
      ? activeLanguage?.language : selectedValue || activeLanguage?.language;
    const currentAWSLanguage = allLanguages.find(lg => lg.language === currentLanguage);

    if (currentAWSLanguage && !currentAWSLanguage.latin) {
      const languageFonts = partialFonts.filter(partialFont => (
        partialFont.languages?.includes(currentAWSLanguage.language || "") ? partialFont : false
      ));
      setParsedFonts(languageFonts.length === 0 ? partialFonts : languageFonts);
    } else {
      setParsedFonts(partialFonts);
    }

    setSelectedFont(partialFonts[0]);
  };

  if (!activeElement || !activeLanguage) return <></>;

  return (
    <div className={classnames({ StylesDisabledContainer: !burnSubtitles })}>
      <LanguageSelectField
        onLanguageChange={onLanguageChange}
        onLoadFonts={loadFonts}
      />
      <SubtitlesFontValues
        activeElement={activeElement}
        onSetSelectedFont={setSelectedFont}
        parsedFonts={parsedFonts}
        preview={preview}
        selectedFont={selectedFont}
      />
      <div className="AlignmentOptions">
        <TextAligment
          activeElement={activeElement}
          onSubtitlePropChange={handleSubtitlePropChange}
          preview={preview}
        />
        <LineHeight
          activeElement={activeElement}
          disabled={!burnSubtitles}
          onSubtitlePropChange={handleSubtitlePropChange}
          preview={preview}
        />
      </div>
      <FontEffects
        activeElement={activeElement}
        disabled={!burnSubtitles}
        onSubtitleEffectChange={handleSubtitleEffectChange}
        onSelectedEffect={handleSelectEffect}
        onSubtitlePropChange={handleSubtitlePropChange}
        organization={organization.name || ""}
        preview={preview}
        selectedEffect={selectedEffect}
      />
    </div>
  );
};

export default SubtitleStyles;
